import type { FeatureFlag } from '@/mocks/saFeatureFlags';
import { flagCategoryMeta } from '@/mocks/saFeatureFlags';

interface FeatureFlagCardProps {
  flag: FeatureFlag;
  onOpenDetail: (flag: FeatureFlag) => void;
}

function EnvDot({ active, env }: { active: boolean; env: string }) {
  return (
    <div className="flex items-center gap-1">
      <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-background-300'}`}></div>
      <span className={`text-[9px] font-medium ${active ? 'text-emerald-600' : 'text-foreground-400'}`}>{env}</span>
    </div>
  );
}

export default function FeatureFlagCard({ flag, onOpenDetail }: FeatureFlagCardProps) {
  const cat = flagCategoryMeta[flag.category];
  const hasDeps = flag.dependencies.length > 0;
  const multiModule = flag.modules.length > 3;
  const prodActive = flag.environments.production;
  const qaActive = flag.environments.qa;
  const devActive = flag.environments.development;

  return (
    <div
      onClick={() => onOpenDetail(flag)}
      className="bg-background-50 border border-background-200 hover:border-background-300 rounded-2xl p-4 cursor-pointer transition-all hover:scale-[1.01] group"
    >
      {/* Top row: category + enabled badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold ${cat.bgColor} ${cat.color}`}>
          <i className={`${cat.icon} text-[9px]`}></i>
          {cat.label}
        </span>
        <div className="flex items-center gap-2">
          {multiModule && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold flex items-center gap-1">
              <i className="ri-alert-line text-[9px]"></i>
              {flag.modules.length} módulos
            </span>
          )}
          <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold ${flag.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-foreground-100 text-foreground-500'}`}>
            {flag.enabled ? 'Activo' : 'Inactivo'}
          </span>
        </div>
      </div>

      {/* Name + description */}
      <h3 className="text-sm font-bold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">{flag.name}</h3>
      <p className="text-foreground-400 text-[11px] leading-relaxed line-clamp-2 mb-3">{flag.description}</p>

      {/* Environment indicators */}
      <div className="flex items-center gap-4 mb-3">
        <EnvDot active={devActive} env="Dev" />
        <EnvDot active={qaActive} env="QA" />
        <EnvDot active={prodActive} env="Prod" />
      </div>

      {/* Bottom row: version + updated + dependencies */}
      <div className="flex items-center justify-between text-[9px] text-foreground-400">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <i className="ri-git-branch-line text-[9px]"></i>
            v{flag.minVersionRequired}
          </span>
          {hasDeps && (
            <span className="flex items-center gap-1 text-primary-600 font-medium">
              <i className="ri-link text-[9px]"></i>
              {flag.dependencies.length} dep{flag.dependencies.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <span className="flex items-center gap-1">
          <i className="ri-time-line text-[9px]"></i>
          {flag.updatedAt}
        </span>
      </div>
    </div>
  );
}